import { ErrorMessage } from '@hookform/error-message';
import logo from '@/assets/images/logo.svg';
import { useEffect, useState, type FC } from 'react';
import { useForm, type FieldErrors } from 'react-hook-form@latest';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';

import { _notNil } from '@/littledash';
import InVivoError from '@/model/InVivoError.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { notAborted } from '@/support/Hooks/fetch/useAbortController';
import { errorToast } from '@/helpers';

import type { MainUser } from '@/model/User.model';
import type { State } from '@/model/State.model';

import useMountedState from '@/support/Hooks/fetch/useMountedState';
import Http from '@/support/http';
import { api as apiRoute, web as webRoute } from '@/support/route';
import { DateUtils } from '@/utils/Date.utils';
import { modalAction } from '@/utils/modal';

import Link from '@/components/UI/Link';

import './CreateAccount.scss';

interface CreateAccountFormData {
  name: string;
  timezone: string;
  password: string;
  password_confirmation: string;
}

interface CreateAccountFormProps {
  currentUser: MainUser;
  timezones: Array<string>;
}

const CreateAccount: FC = () => {
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const isMounted = useMountedState();

  const { timezones, currentUser } = useSelector(({ user: { currentUser, timezones } }: State) => ({
    timezones,
    currentUser,
  }));

  const getTimezones = async (signal: AbortSignal) => {
    setLoading(true);
    try {
      const { data } = await Http.get<Array<string>>(apiRoute('config.timezones'), { signal });
      dispatch({
        type: 'USER_SET_TIMEZONES',
        timezones: data,
      });
    } catch (error) {
      if (notAborted(error)) {
        ExceptionHandler.captureException(
          new InVivoError('Failed to load timezones', { cause: error, slug: 'create-account-load-timezones' })
        );
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    getTimezones(abortController.signal);
    return () => {
      abortController.abort();
    };
  }, []);

  return (
    <>
      <header className="no-auth-header center w-100 mv4 mt6-l">
        <img className="dib" src={logo} style={{ width: 150, height: 60 }} alt="Benchling In Vivo" />
      </header>
      <div className="center createAccount">
        <div className="ui-card pa3 mb3">
          <h3>Create Your Account</h3>
          {!loading && _notNil(currentUser) && <CreateAccountForm currentUser={currentUser} timezones={timezones} />}
        </div>
        <Link
          className="link blue"
          data-testid="logout-link"
          onClick={() => {
            dispatch({ type: 'LOGOUT' });
            dispatch({ type: 'TEAM_SET_TEAM', team: null });
            dispatch({ type: 'USER_SET_CURRENT_USER', currentUser: null });
          }}
        >
          &larr; Log out
        </Link>
      </div>
    </>
  );
};

const MINIMUM_ZXCVBN_SCORE = 2;

const FieldError: FC<{ errors: FieldErrors<CreateAccountFormData>; name: keyof CreateAccountFormData }> = ({
  errors,
  name,
}) => <ErrorMessage errors={errors} name={name} render={({ message }) => <p className="f6 red db pt2">{message}</p>} />;

const CreateAccountForm: FC<CreateAccountFormProps> = ({ currentUser, timezones }) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { closeModal } = modalAction(dispatch);
  const navigate = useNavigate();

  const {
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = useForm<CreateAccountFormData>({
    reValidateMode: 'onSubmit',
    defaultValues: {
      timezone: currentUser.timezone === 'UTC' ? DateUtils.browserTimezone() : currentUser.timezone,
    },
  });

  const onSubmit = async (data: CreateAccountFormData) => {
    setLoading(true);
    try {
      await Http.put(apiRoute('user.update'), data);
      closeModal();
      dispatch({
        type: 'USER_SET_CURRENT_USER',
        currentUser: { ...currentUser, name: data.name, timezone: data.timezone },
      });
      navigate(webRoute('dashboard'));
    } catch (error) {
      errorToast('Failed to create account. Please try again.');
      ExceptionHandler.captureException(
        new InVivoError('Failed to create account', { cause: error, slug: 'create-account-submit' })
      );
    } finally {
      setLoading(false);
    }
  };

  const checkPasswordStrength = async (password: string): Promise<string | true> => {
    const { data } = await Http.post<{ score: number; feedback: { suggestions: Array<string> } }>(
      apiRoute('password_resets.evaluate'),
      { password }
    );
    if (data.score < MINIMUM_ZXCVBN_SCORE) {
      return `Password too weak: ${data.feedback.suggestions.join(' ')}`;
    }
    return true;
  };

  const hasError = (field: keyof CreateAccountFormData): boolean => _notNil(errors[field]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-test-component="CreateAccountForm" data-test-element="container">
      <div>
        <label htmlFor="email">Email</label>
        <input id="email" type="text" autoComplete="email" value={currentUser.email} disabled readOnly />
      </div>

      <div className={cn({ hasError: hasError('name') })}>
        <label htmlFor="name">Full Name</label>
        <input
          id="name"
          type="text"
          autoComplete="name"
          data-test-component="CreateAccountForm"
          data-test-element="name-input"
          disabled={loading}
          className={cn({ input__error: hasError('name') })}
          {...register('name', {
            required: "Name can't be blank",
          })}
        />
        <FieldError errors={errors} name="name" />
      </div>

      <div className={cn({ hasError: hasError('timezone') })}>
        <label htmlFor="timezone">Timezone</label>
        <select
          id="timezone"
          style={{ marginBottom: 0 }}
          data-test-component="CreateAccountForm"
          data-test-element="timezone-select"
          {...register('timezone', {
            required: 'Required',
            validate: {
              validTimezone: (value: string) => (timezones.includes(value) ? true : `Unsupported timezone [${value}]`),
            },
          })}
        >
          {timezones.map((timezone) => (
            <option key={timezone} value={timezone}>
              {timezone}
            </option>
          ))}
        </select>
        <FieldError errors={errors} name="timezone" />
      </div>

      <div className={cn('mt3', { hasError: hasError('password') })}>
        <label htmlFor="password">Password</label>
        <input
          id="password"
          type="password"
          autoComplete="new-password"
          data-test-component="CreateAccountForm"
          data-test-element="password-input"
          disabled={loading}
          className={cn({ input__error: hasError('password') })}
          {...register('password', {
            required: 'Enter a new password',
            validate: {
              isStrongPassword: checkPasswordStrength,
            },
          })}
        />
        <FieldError errors={errors} name="password" />
      </div>

      <div className={cn({ hasError: hasError('password_confirmation') })}>
        <label htmlFor="password_confirmation">Confirm Password</label>
        <input
          id="password_confirmation"
          type="password"
          autoComplete="new-password"
          data-test-component="CreateAccountForm"
          data-test-element="password-confirm-input"
          disabled={loading}
          className={cn({ input__error: hasError('password_confirmation') })}
          {...register('password_confirmation', {
            required: 'Enter your password again',
            validate: (value: string) => value === watch('password') || 'Must be the same as your new password.',
          })}
        />
        <FieldError errors={errors} name="password_confirmation" />
      </div>

      <button type="submit" disabled={loading} data-test-component="CreateAccountForm" data-test-element="save-button">
        Save
      </button>
    </form>
  );
};

export default CreateAccount;
